import { useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import DropdownPicker from './ui/DropdownPicker';
import { getVenuesByOrgID, getShowsByOrgID } from '../../util/util';
import { getShowViewsByShowIds, getSeenShowsByShowIds, getShowImpressionsByShowIds } from '../../util/db';
import { aggregateByGranularity, aggregateImpressionsByGranularity, createBucketSkeleton, bucketKeyForDate, formatDate, groupShowsByStatus, nyCalendarDate } from '../../util/data-factory';
import { ShowTableGroup } from './ShowTable';
import { IconTooltip } from './ui/TooltipCard';
import ToggleButton from './ui/ToggleButton';
import AnalyticsChart from './AnalyticsChart';

// Views/impressions tracking start date
const IMPRESSIONS_LAUNCH_DATE = '2026-09-01';

const PERIODS = [
  { label: '30d', days: 30  },
  { label: '3m',  days: 90  },
  { label: '6m',  days: 180 },
  { label: '1y',  days: 365 },
];

const GRANULARITIES = ['Day', 'Week', 'Month'];

const countImpressions = ({ count }) => count ?? 0;

function KpiStat({ label, value, loading, valueClassName, info }) {
  return (
    <div className="w-36">
      <div className="flex items-center gap-1">
        <p className="text-sm text-slate-400 uppercase tracking-wide">{label}</p>
        {info}
      </div>
      {loading
        ? <div className="h-10 w-20 bg-slate-200 rounded animate-pulse mt-1" />
        : <p className={`text-4xl font-bold mt-1 ${valueClassName}`}>{value.toLocaleString()}</p>
      }
    </div>
  );
}

function KpiDivider() {
  return <p className="hidden sm:block text-2xl font-light text-slate-300 pb-1">|</p>;
}

export const DataContent = forwardRef(function DataContent({ effectiveOrgId }, ref) {
  const [venues, setVenues]                   = useState([]);
  const [selectedVenueId, setSelectedVenueId] = useState(null);
  const [shows, setShows]                     = useState([]);
  const [viewRows, setViewRows]               = useState([]);
  const [seenRows, setSeenRows]               = useState([]);
  const [impressionRows, setImpressionRows]   = useState([]);
  const [granularity, setGranularity]         = useState('week');
  const [periodDays, setPeriodDays]           = useState(90);
  const [showsLoaded, setShowsLoaded]         = useState(false);
  const [loading, setLoading]                 = useState(true);
  const [loadError, setLoadError]             = useState(null);
  const [visibleLines, setVisibleLines]       = useState({ impressions: true, views: true, seen: true, openings: true });

  const toggleLine = useCallback(key => setVisibleLines(prev => ({ ...prev, [key]: !prev[key] })), []);

  const handleLoadError = useCallback(err => {
    console.error(err);
    setLoadError('Failed to load analytics data.');
    setLoading(false);
  }, []);

  const fromDate = useMemo(() => {
    const d = nyCalendarDate(new Date()); // today, in the gallery's own (NY) calendar
    d.setUTCDate(d.getUTCDate() - periodDays);
    return d;
  }, [periodDays]);

  useEffect(() => {
    if (!effectiveOrgId) return;
    setLoading(true);
    setLoadError(null);
    setVenues([]);
    setShows([]);
    setViewRows([]);
    setSeenRows([]);
    setImpressionRows([]);
    setShowsLoaded(false);
    setSelectedVenueId(null);

    let cancelled = false;
    Promise.all([getVenuesByOrgID(effectiveOrgId), getShowsByOrgID(effectiveOrgId)])
      .then(([v, s]) => {
        if (cancelled) return;
        setVenues(v ?? []);
        setShows(s);
        setShowsLoaded(true);
      })
      .catch(err => { if (!cancelled) handleLoadError(err); });
    return () => { cancelled = true; };
  }, [effectiveOrgId, handleLoadError]);

  useEffect(() => {
    if (!showsLoaded) return; // shows fetch hasn't resolved yet — don't touch loading
    if (!shows.length) { setViewRows([]); setSeenRows([]); setImpressionRows([]); setLoading(false); return; }
    setLoading(true);

    const allShowIds = shows.map(s => s.show_id);
    Promise.all([
      getShowViewsByShowIds(allShowIds, fromDate),
      getSeenShowsByShowIds(allShowIds, fromDate),
      getShowImpressionsByShowIds(allShowIds, fromDate).catch(err => { console.error(err); return []; }),
    ])
      .then(([views, seen, impressions]) => {
        setViewRows(views);
        setSeenRows(seen);
        setImpressionRows(impressions);
        setLoading(false);
      })
      .catch(handleLoadError);
  }, [shows, showsLoaded, fromDate, handleLoadError]);

  const activeShows = useMemo(
    () => (selectedVenueId ? shows.filter(s => String(s.venue_id) === String(selectedVenueId)) : shows),
    [shows, selectedVenueId]
  );

  const { filteredViewRows, filteredSeenRows, filteredImpressionRows } = useMemo(() => {
    const activeShowIdSet = new Set(activeShows.map(s => s.show_id));
    const isActive = r => activeShowIdSet.has(r.show_id);
    return {
      filteredViewRows: viewRows.filter(isActive),
      filteredSeenRows: seenRows.filter(isActive),
      filteredImpressionRows: impressionRows.filter(r => isActive(r) && r.view_date >= IMPRESSIONS_LAUNCH_DATE),
    };
  }, [activeShows, viewRows, seenRows, impressionRows]);

  const impressionsLaunchBucket = useMemo(
    () => bucketKeyForDate(new Date(IMPRESSIONS_LAUNCH_DATE), granularity),
    [granularity]
  );

  const chartData = useMemo(() => {
    const showOpeningsByBucket = {};
    activeShows.forEach(show => {
      if (show.merged_into || !show.start_date) return;
      const key = bucketKeyForDate(new Date(show.start_date), granularity);
      (showOpeningsByBucket[key] ??= []).push(show.display_title || show.show_title);
    });

    const toDate = nyCalendarDate(new Date()); // today, in the gallery's own (NY) calendar
    const bucketSkeleton = createBucketSkeleton(granularity, fromDate, toDate);
    const viewsAgg       = aggregateByGranularity(filteredViewRows, granularity, fromDate, toDate, bucketSkeleton);
    const seenAgg        = aggregateByGranularity(filteredSeenRows, granularity, fromDate, toDate, bucketSkeleton);
    const impressionsAgg = aggregateImpressionsByGranularity(filteredImpressionRows, granularity, fromDate, toDate, bucketSkeleton);
    const seenMap        = Object.fromEntries(seenAgg.map(d => [d.date, d.views]));
    const impressionsMap = Object.fromEntries(impressionsAgg.map(d => [d.date, d.views]));
    return viewsAgg.map(d => ({
      date: d.date,
      views: d.views,
      seen: seenMap[d.date] ?? 0,
      impressions: d.date >= impressionsLaunchBucket ? (impressionsMap[d.date] ?? 0) : null,
      openings: showOpeningsByBucket[d.date] ?? [],
    }));
  }, [activeShows, filteredViewRows, filteredSeenRows, filteredImpressionRows, granularity, fromDate, impressionsLaunchBucket]);

  const tableGroups = useMemo(() => {
    const canonicalIdById = Object.fromEntries(shows.map(s => [s.show_id, s.merged_into ?? s.show_id]));
    const sumByShow = (rows, amountOf) => {
      const totals = {};
      rows.forEach(row => {
        const id = canonicalIdById[row.show_id] ?? row.show_id;
        totals[id] = (totals[id] || 0) + amountOf(row);
      });
      return totals;
    };
    const viewMap = sumByShow(filteredViewRows, () => 1);
    const seenMap = sumByShow(filteredSeenRows, () => 1);
    const impressionMap = sumByShow(filteredImpressionRows, countImpressions);

    const rows = activeShows
      .filter(show => !show.merged_into)
      .map(show => ({
        show_id:           show.show_id,
        display_title:     show.display_title,
        show_title:        show.show_title,
        venue_name:        show.venues?.venue_name ?? '',
        venue_address:     show.venues?.address1 ?? '',
        venue_neighborhood: show.venues?.neighborhood ?? '',
        start_date:        show.start_date,
        end_date:          show.end_date,
        artist_names:      show.artist_names ?? [],
        is_archived:       show.is_archived ?? false,
        views:             viewMap[show.show_id] ?? 0,
        seen:              seenMap[show.show_id] ?? 0,
        impressions:       impressionMap[show.show_id] ?? 0,
      }))
      .filter(row => row.views > 0 || row.seen > 0 || row.impressions > 0)
      .sort((a, b) => b.impressions - a.impressions);

    return groupShowsByStatus(rows);
  }, [shows, activeShows, filteredViewRows, filteredSeenRows, filteredImpressionRows]);

  const filterVenues = useMemo(() => {
    const venueIdsWithShows = new Set(shows.map(s => String(s.venue_id)));
    return venues
      .filter(v => !v.is_archived || venueIdsWithShows.has(String(v.venue_id)))
      .sort((a, b) => Number(!!a.is_archived) - Number(!!b.is_archived));
  }, [venues, shows]);

  const hasTableRows = tableGroups.live.length > 0 || tableGroups.past.length > 0 || tableGroups.hidden.length > 0;
  const totalImpressions = filteredImpressionRows.reduce((sum, row) => sum + countImpressions(row), 0);
  const multiVenue = filterVenues.length > 1;
  const tableProps = { variant: 'analytics', showVenueColumn: multiVenue && !selectedVenueId };

  useImperativeHandle(ref, () => ({
    isLoading: () => loading,
    isMultiVenue: () => multiVenue,
    clickPeriod: days => setPeriodDays(days),
    clickGranularity: g => setGranularity(g),
    toggleImpressionsLine: () => toggleLine('impressions'),
  }), [loading, multiVenue, toggleLine]);

  if (loadError) {
    return <p className="mx-8 mt-8 text-sm text-slate-400">{loadError}</p>;
  }

  return (
    <div className="mx-8 space-y-8 pb-8">

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-4">

        {multiVenue && (
          <div data-tour="analytics-venue-filter">
            <DropdownPicker
              options={filterVenues.map(v => ({
                key: String(v.venue_id),
                label: v.venue_name,
                sublabel: [v.address1, v.neighborhood].filter(Boolean).join(' · ') || undefined,
                badges: [v.is_archived && 'Archived', v.is_closed && 'Closed'].filter(Boolean),
              }))}
              value={selectedVenueId}
              onChange={setSelectedVenueId}
              allLabel="All Venues"
              borderClass="border-secondary-red/30 hover:border-secondary-red transition-colors"
            />
          </div>
        )}

        {/* Period */}
        <div className="join" data-tour="analytics-period-group">
          {PERIODS.map(({ label, days }) => (
            <ToggleButton
              key={days}
              active={periodDays === days}
              onClick={() => setPeriodDays(days)}
              data-tour={days === 30 ? 'period-30d' : undefined}
            >
              {label}
            </ToggleButton>
          ))}
        </div>

        {/* Granularity */}
        <div className="join" data-tour="analytics-granularity-group">
          {GRANULARITIES.map(g => (
            <ToggleButton
              key={g}
              active={granularity === g.toLowerCase()}
              onClick={() => setGranularity(g.toLowerCase())}
              data-tour={g === 'Day' ? 'granularity-day' : undefined}
            >
              {g}
            </ToggleButton>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-4" data-tour="analytics-kpis">
        <KpiStat
          label="Total Views"
          value={totalImpressions}
          loading={loading}
          valueClassName="text-slate-700"
          info={
            <IconTooltip
              position="bottom-right"
              label={<>Views tracking starts {formatDate(IMPRESSIONS_LAUNCH_DATE, true)}<br />Data from before that date isn't shown.</>}
            >
              <InformationCircleIcon className="h-3.5 w-3.5 text-slate-300" aria-hidden="true" />
            </IconTooltip>
          }
        />
        <KpiDivider />
        <KpiStat label="Total Clicks" value={filteredViewRows.length} loading={loading} valueClassName="text-slate-500" />
        <KpiDivider />
        <KpiStat label="Total Seen" value={filteredSeenRows.length} loading={loading} valueClassName="text-slate-400" />
      </div>

      <AnalyticsChart
        chartData={chartData}
        granularity={granularity}
        loading={loading}
        visibleLines={visibleLines}
        onToggleLine={toggleLine}
        impressionsLaunchBucket={impressionsLaunchBucket}
      />

      {hasTableRows && (
        <div className={`space-y-8 transition-opacity duration-200 ${loading ? 'opacity-40 pointer-events-none' : ''}`}>
          <ShowTableGroup title="Live Shows" rows={tableGroups.live} {...tableProps} />
          <ShowTableGroup title="Past Shows" rows={tableGroups.past} {...tableProps} />
          <ShowTableGroup title="Hidden Shows" rows={tableGroups.hidden} {...tableProps} />
        </div>
      )}

      {/* Empty state — only shown after loading finishes with no data */}
      {!loading && !hasTableRows && (
        <p className="text-slate-400 text-sm">No data for this period.</p>
      )}

    </div>
  );
});
